import {
    FormControlLabel,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import React, { ChangeEvent, useMemo, useState } from 'react';
import T from 'src/components/content-manager/translator/T';
import { getUnitFactor } from 'src/entities/cart/response/OrderProductListGrouped';
import { DiscountEntry } from 'src/entities/volume-discount/Discounts';
import VolumeDiscount from 'src/entities/volume-discount/VolumeDiscount';
import { VolumeDiscountTableRow } from './VolumeDiscountTableRow';

const StyledTable = styled(Table)({
    '& [class*="MuiTableCell-root"]': {
        border: 'none',
    },
});

const LIMITED = 4;

interface VolumeDiscountTableProps {
    volumeDiscount: VolumeDiscount;
    basePrice?: number;
    currencyIso?: string;
    currentColliLevel?: number;
    limitVisibleDiscounts?: boolean;
}

export const VolumeDiscountTable: React.FC<VolumeDiscountTableProps> = ({
    basePrice,
    currencyIso,
    volumeDiscount,
    currentColliLevel,
    limitVisibleDiscounts,
}) => {
    const [showAll, setShowAll] = useState(false);

    const [visibleDiscounts, previousDiscounts, nextDiscounts] = useMemo(() => {
        const discounts: (DiscountEntry & { active?: boolean })[] = [];

        // Add sorted discounts
        discounts.push(...volumeDiscount.activePremiumLevelDiscounts);

        // Find active discount
        let activeIndex = -1;
        if (currentColliLevel != null)
            for (const discount of discounts) {
                const unitFactor = volumeDiscount.baseColli
                    ? getUnitFactor(volumeDiscount.baseColli, discount.colli)
                    : 1;
                if (currentColliLevel >= discount.minQuantity * unitFactor) activeIndex++;
                else break;
            }

        // Mark active discount
        if (activeIndex >= 0) discounts[activeIndex].active = true;

        // Limit discounts if requested
        if (!limitVisibleDiscounts) return [discounts, [], []];

        // Start 1 below active discount
        let startIndex = Math.max(0, activeIndex - 1);
        // Limit to discountItemLimit items
        const endIndex = Math.min(discounts.length, startIndex + LIMITED);
        // Amend start to show discountItemLimit items (if possible)
        if (endIndex - startIndex < LIMITED) startIndex = Math.max(0, endIndex - LIMITED);

        return [discounts.slice(startIndex, endIndex), discounts.slice(0, startIndex), discounts.slice(endIndex)];
    }, [currentColliLevel, limitVisibleDiscounts, volumeDiscount]);

    const handleChangeShowAll = (e: ChangeEvent<HTMLInputElement>) => setShowAll(e.target.checked);

    return (
        <StyledTable size="small" padding="none">
            <TableHead>
                <TableRow>
                    <TableCell variant="head">
                        <T k="shop.volume_discount.scale" />
                    </TableCell>
                    <TableCell variant="head">
                        {volumeDiscount.activePremiumLevel <= 0 ? (
                            <T k="shop.volume_discount.calculated_discount" />
                        ) : (
                            <T k="shop.volume_discount.calculated_premium_discount" />
                        )}
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {showAll &&
                    previousDiscounts.map((discount, i) => (
                        <VolumeDiscountTableRow
                            key={i}
                            discount={discount}
                            basePrice={basePrice}
                            currencyIso={currencyIso}
                        />
                    ))}
                {visibleDiscounts.map((discount, i) => (
                    <VolumeDiscountTableRow
                        key={i}
                        discount={discount}
                        basePrice={basePrice}
                        currencyIso={currencyIso}
                    />
                ))}
                {showAll &&
                    nextDiscounts.map((discount, i) => (
                        <VolumeDiscountTableRow
                            key={i}
                            discount={discount}
                            basePrice={basePrice}
                            currencyIso={currencyIso}
                        />
                    ))}
            </TableBody>
            {previousDiscounts.length + nextDiscounts.length > 0 && (
                <TableFooter>
                    <TableRow>
                        <TableCell variant="footer" colSpan={2}>
                            <FormControlLabel
                                label={<T k="shop.volume_discount.show_all" />}
                                control={<Switch color="primary" checked={showAll} onChange={handleChangeShowAll} />}
                            />
                        </TableCell>
                    </TableRow>
                </TableFooter>
            )}
        </StyledTable>
    );
};
